import {
	Table,
	TableRow,
	TableColumn,
	Text,
	TableHeader,
	TableHeaderColumn,
	Tooltip,
	Flex,
	Icon
} from '@upsales/components';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';
import { formatCurrency, formatValue } from '../Helpers/Helpers';
import ClientIndustryResource from 'Resources/ClientIndustry';
import React, { useState, useEffect, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import { useIndustryInsights } from '../Context/Context';
import EmptyData from '../EmptyData/EmptyData';
import Error from 'App/resources/Model/Error';
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import './KeyFiguresTable.scss';

interface Props {
	clientId: number;
	clientName: string;
	code: string;
}

const KeyFiguresTable = ({ clientId, clientName, code }: Props) => {
	const {
		state: { bucketRange, description, keyFiguresLoading, hasKeyFiguresData },
		setKeyFiguresLoading,
		setHasKeyFiguresData
	} = useIndustryInsights();
	const [keyFigures, setKeyFigures] = useState<{
		client: { metrics: any };
		industry: { metrics: any; outliers: any };
	}>({
		client: { metrics: {} },
		industry: { metrics: {}, outliers: {} }
	});
	const keyFiguresRequest = useRef<null | CancelablePromise<{ data: any }>>(null);

	useEffect(() => {
		setKeyFiguresLoading(true);

		const useSNI = Tools.FeatureHelper.hasSoftDeployAccess('AI_CLIENT_INDUSTRY_SNI');
		const group = !useSNI ? code.substring(0, 3) : undefined;

		const request = makeCancelable(ClientIndustryResource.getKeyFigures(clientId, group));
		keyFiguresRequest.current = request;

		request.promise
			.then(({ data }) => {
				const hasValidData = data?.client?.name !== null && data?.industry?.outliers?.totalSamples > 0;
				setKeyFigures(data);
				setHasKeyFiguresData(hasValidData);
			})
			.catch((err: Error) => {
				logError(err);
				setHasKeyFiguresData(false);
			})
			.finally(() => {
				setKeyFiguresLoading(false);
			});

		return () => {
			keyFiguresRequest.current?.cancel();
		};
	}, [clientId, code]);

	const classes = new BemClass('KeyFiguresTable');
	const FRACTION_DIGITS = 2;
	const lang = {
		keyFiguresTableTitle: T('ai.industryInsights.keyFiguresTableTitle', { client: clientName }),
		similarCompanies: T('default.similarCompanies'),
		inIndustry: T('default.inIndustry'),
		revenueGrowth: T('default.revenueGrowth'),
		operatingMargin: T('default.operatingMargin'),
		netDebt: T('default.netDebt'),
		ebitda: T('default.ebitda'),
		employmentRate: T('default.employmentRate'),
		netSalesPerEmployee: T('default.netSalesPerEmployee'),
		extremeValuesFound: T('ai.industryInsights.extremeValuesFound'),
		similarCompaniesTooltip: T('ai.industryInsights.similarCompaniesTooltip', {
			value: bucketRange ? `${formatCurrency(bucketRange.minRev)} - ${formatCurrency(bucketRange.maxRev)}` : '',
			industry: description
		}),
		clientExtremeValue: T('ai.industryInsights.clientExtremeValue'),
		clientExtremeValueContext: T('ai.industryInsights.clientExtremeValueContext', {
			client: clientName,
			avg: keyFigures.industry.metrics.netDebtEbitdaRatio?.toFixed(FRACTION_DIGITS)
		}),
		netDebtEbitdaDescription: T('ai.industryInsights.netDebtEbitdaDescription'),
		negativeEbitdaWarning: T('ai.industryInsights.negativeEbitdaWarning'),
		negativeNetDebtInfo: T('ai.industryInsights.negativeNetDebtInfo')
	};

	const getValueColor = (value: number | undefined, format: 'percentage' | 'multiplier' | 'currency') => {
		if (value === undefined) return undefined;

		if (format === 'multiplier') {
			return value >= 2.5 ? 'danger-4' : 'success-5';
		}

		const isNegative = value < 0;
		const isPositive = value > 0;

		if (isNegative) return 'danger-4';
		if (isPositive) return 'success-5';

		return undefined;
	};

	const isExtremeValue = (value: number | undefined) => {
		return value !== undefined && Math.abs(value) > 100;
	};

	const getNetDebtEbitdaTooltip = () => {
		if (isExtremeValue(keyFigures.client.metrics.netDebtEbitdaRatio)) {
			return lang.clientExtremeValueContext;
		}
		return undefined;
	};

	const getNetDebtEbitdaIcon = () => {
		if (isExtremeValue(keyFigures.client.metrics.netDebtEbitdaRatio)) {
			return <Icon data-testid="net-debt-warning" name="warning" color="bright-yellow" />;
		}
		return null;
	};

	const MIN_SAMPLE_SIZE = 5;

	const getIndustryValue = (value: number | undefined, sampleCount: number | undefined) => {
		if (!sampleCount || sampleCount < MIN_SAMPLE_SIZE) {
			return undefined;
		}
		return value;
	};

	const rows = [
		{
			label: lang.revenueGrowth,
			clientValue: keyFigures.client.metrics.growth,
			industryValue: getIndustryValue(
				keyFigures.industry.metrics.growth,
				keyFigures.industry.metrics.growthSampleCount
			),
			format: 'percentage' as const
		},
		{
			label: lang.operatingMargin,
			clientValue: keyFigures.client.metrics.operatingMargin,
			industryValue: getIndustryValue(
				keyFigures.industry.metrics.operatingMargin,
				keyFigures.industry.metrics.operatingMarginSampleCount
			),
			format: 'percentage' as const
		},
		{
			label: `${lang.netDebt} /  ${lang.ebitda}`,
			clientValue:
				keyFigures.client.metrics.netDebtEbitdaRatio !== undefined &&
				keyFigures.client.metrics.netDebtEbitdaRatio <= 0
					? undefined
					: keyFigures.client.metrics.netDebtEbitdaRatio,
			industryValue: getIndustryValue(
				keyFigures.industry.metrics.netDebtEbitdaRatio,
				keyFigures.industry.metrics.netDebtEbitdaSampleCount
			),
			format: 'multiplier' as const,
			tooltip: getNetDebtEbitdaTooltip(),
			icon: getNetDebtEbitdaIcon()
		},
		{
			label: lang.employmentRate,
			clientValue: keyFigures.client.metrics.employmentRate,
			industryValue: getIndustryValue(
				keyFigures.industry.metrics.employmentRate,
				keyFigures.industry.metrics.employmentRateSampleCount
			),
			format: 'percentage' as const
		},
		{
			label: lang.netSalesPerEmployee,
			clientValue: keyFigures.client.metrics.netSalesPerEmployee,
			industryValue: getIndustryValue(
				keyFigures.industry.metrics.netSalesPerEmployee,
				keyFigures.industry.metrics.netSalesPerEmployeeSampleCount
			),
			format: 'currency' as const
		}
	];

	// Adds cool loading state to look like a table
	if (keyFiguresLoading) {
		return (
			<Table className={classes.b()}>
				{[...Array(5)].map((_, i) => (
					<TableRow key={`loading-${i}`}>
						{[...Array(3)].map((_, j) => (
							<TableColumn key={`loading-${i}-${j}`}>
								<Text loading />
							</TableColumn>
						))}
					</TableRow>
				))}
			</Table>
		);
	}

	return (
		<Flex direction="column" space="ptl pbs" gap="u2" className={classes.b()}>
			{hasKeyFiguresData ? (
				<>
					<Text space="pll prl" bold>
						{lang.keyFiguresTableTitle}
					</Text>
					<Table className={classes.elem('table').b()}>
						<TableHeader className={classes.elem('header').b()}>
							<TableHeaderColumn>
								<Text size="sm">&nbsp;</Text>
							</TableHeaderColumn>
							<TableHeaderColumn>
								<Text size="sm">{clientName}</Text>
							</TableHeaderColumn>
							<TableHeaderColumn>
								<Flex gap="u2" alignItems="center">
									<Text size="sm">
										{lang.similarCompanies} {lang.inIndustry.toLowerCase()}
									</Text>
									<Tooltip title={lang.similarCompaniesTooltip}>
										<Icon name="info-circle" loading={keyFiguresLoading} />
									</Tooltip>
								</Flex>
							</TableHeaderColumn>
						</TableHeader>
						{rows.map(row => (
							<TableRow className={classes.elem('row').b()} key={row.label}>
								<TableColumn>
									{row.label === `${lang.netDebt} /  ${lang.ebitda}` ? (
										<Flex gap="u2" alignItems="center">
											<Text>{row.label}</Text>
											<Tooltip title={lang.netDebtEbitdaDescription}>
												<Icon name="info-circle" />
											</Tooltip>
										</Flex>
									) : (
										<Text>{row.label}</Text>
									)}
								</TableColumn>
								<TableColumn>
									<Flex gap="u2" alignItems="center">
										<Text color={getValueColor(row.clientValue, row.format)}>
											{formatValue(row.clientValue, row.format)}
										</Text>
										{row.tooltip && row.icon && <Tooltip title={row.tooltip}>{row.icon}</Tooltip>}
									</Flex>
								</TableColumn>
								<TableColumn>
									<Flex gap="u2" alignItems="center">
										<Text>{formatValue(row.industryValue, row.format)}</Text>
										{row.label === `${lang.netDebt} /  ${lang.ebitda}` &&
										keyFigures.industry.outliers?.count > 0 ? (
											<Tooltip title={lang.extremeValuesFound}>
												<Icon data-testid="net-debt-info" name="info-circle" />
											</Tooltip>
										) : null}
									</Flex>
								</TableColumn>
							</TableRow>
						))}
					</Table>
				</>
			) : (
				<EmptyData />
			)}
		</Flex>
	);
};

export default KeyFiguresTable;
